import _ from "lodash";
import React, { useState, useRef, memo, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import {
  Modal,
  Avatar,
  Button,
  EmptyState,
  SearchInput,
  Checkbox,
} from "@toddle-edu/ds-web";
import { NoSearchResultsIllustration } from "@toddle-edu/ds-theme";

import { compose, graphql } from "lib/apolloClient";
import { useDebouncedCallback } from "Utils/CustomHooks";
import { getAvatarColor } from "Utils/ColorHelper";
import { getTeacherFeedQuery } from "AddOnManager/modules/AddOnManagerQueries";
import withLoader from "UIComponents/GqlWithLoader";
import { useI18n } from "Hooks";
import { useDispatch } from "Hooks/useDispatch";

import {
  USER,
  MESSAGING,
  MESSAGING_MODULES,
} from "../../../modules/OrgAddOnContants";
import {
  updateAddOnStatus,
  getQueryVariablesForMessaging,
} from "../../../modules/OrgAddOnHelpers";

import classes from "./StaffList.scss";

const styles = {
  modalBodyStyle: {
    padding: "0",
  },
};

const ListItem = memo(function ListItem(props) {
  const { user, isStaffSelected, toggleSelectedState } = props;
  const { id: userId, firstName, lastName, profileImage, email } = user;

  const fullName = _.trim(`${firstName} ${lastName}`);
  return (
    <li className={classes.listItem}>
      <div className={classes.userDetails}>
        <Avatar
          id={userId}
          name={fullName}
          src={profileImage}
          shade="light"
          size="medium"
          color={getAvatarColor(userId)}
        />
        <div>
          <div
            className={classNames(
              "text-body-1",
              "text-textSubtle",
              classes.userName
            )}
          >
            {fullName}
          </div>
          <div
            className={classNames(
              "text-body-3",
              "text-textSubtle",
              classes.userEmail
            )}
          >
            {email}
          </div>
        </div>
      </div>
      <Checkbox
        dsVersion="2.0"
        isChecked={isStaffSelected}
        onChange={() => toggleSelectedState(userId)}
      />
    </li>
  );
});

const getSortedUserList = ({ userList, selectedStaffIdsSet }) => {
  return _.sortBy(userList, [
    user => !selectedStaffIdsSet.has(user?.node?.id),
    user => _.toLower(user?.node?.firstName),
    user => _.toLower(user?.node?.lastName),
  ]);
};

const List = ({ staffEdges, selectedStaffIdsSet, setSelectedStaffIdsSet }) => {
  const scrollContainer = useRef();
  const { t } = useI18n(["common"]);
  const [searchText, setSearchText] = useState("");

  const [onSearchChangeDebounced] = useDebouncedCallback(
    searchText => {
      setSearchText(searchText);
    },
    300,
    []
  );

  const toggleSelectedState = useCallback(
    userId => {
      setSelectedStaffIdsSet(prevState => {
        const newState = new Set(prevState);
        if (newState.has(userId)) newState.delete(userId);
        else newState.add(userId);
        return newState;
      });
    },
    [setSelectedStaffIdsSet]
  );

  const filteredItems = useMemo(() => {
    const trimmedText = searchText.trim().toLowerCase();
    if (!trimmedText) {
      return getSortedUserList({ userList: staffEdges, selectedStaffIdsSet });
    }

    const result = _.filter(staffEdges, ({ node }) => {
      const { firstName, lastName, email } = node;
      const fullName = `${firstName} ${lastName}`;
      return (
        _.includes(fullName.trim().toLowerCase(), trimmedText) ||
        _.includes(email.trim().toLowerCase(), trimmedText)
      );
    });
    return getSortedUserList({ userList: result, selectedStaffIdsSet });
    // !NOTE: Explicity didn't pass selectedStaffIdsSet to dependency array, as
    // do not want to reorder the list on every selectedStaffIdsSet change
  }, [searchText, staffEdges]);

  const isAllFilteredChecked = useMemo(() => {
    return _.every(filteredItems, ({ node }) =>
      selectedStaffIdsSet.has(node.id)
    );
  }, [filteredItems, selectedStaffIdsSet]);

  const isAllFilteredIndeterminate = useMemo(() => {
    return (
      !isAllFilteredChecked &&
      _.some(filteredItems, ({ node }) => selectedStaffIdsSet.has(node.id))
    );
  }, [filteredItems, selectedStaffIdsSet, isAllFilteredChecked]);

  const onSelectAllChange = () => {
    if (isAllFilteredChecked) {
      // All of the filtered items are checked, so uncheck all of them
      setSelectedStaffIdsSet(prev => {
        const newState = new Set(prev);
        _.forEach(filteredItems, ({ node }) => {
          newState.delete(node.id);
        });
        return newState;
      });
      return;
    }

    // Some or none of the filtered items are checked, so check all of them
    setSelectedStaffIdsSet(prev => {
      const newState = new Set(prev);
      _.forEach(filteredItems, ({ node }) => {
        newState.add(node.id);
      });
      return newState;
    });
  };

  return (
    <div className={classes.contentContainer}>
      <div className={classes.searchContainer}>
        <SearchInput
          size={"medium"}
          onChange={onSearchChangeDebounced}
          placeholder={t("common:search_by_name_or_email")}
        />
      </div>
      {_.isEmpty(filteredItems) ? (
        <EmptyState illustration={NoSearchResultsIllustration} />
      ) : (
        <ul className={classes.listContainer} ref={scrollContainer}>
          <li className={classes.listItem}>
            <div className={classNames("text-label-2", classes.userName)}>
              {_.trim(searchText)
                ? t("common:select_all_matching_admins")
                : t("common:select_all_admins")}
            </div>
            <Checkbox
              dsVersion="2.0"
              onChange={onSelectAllChange}
              isChecked={isAllFilteredChecked}
              isIndeterminate={isAllFilteredIndeterminate}
            />
          </li>
          {_.map(filteredItems, ({ node }) => (
            <ListItem
              key={node.id}
              user={node}
              isStaffSelected={selectedStaffIdsSet.has(node.id)}
              toggleSelectedState={toggleSelectedState}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

const STABLE_EMPTY_ARRAY = [];
const ListWithData = compose(
  graphql(getTeacherFeedQuery, {
    name: "getTeacherFeed",
    options: ({ orgId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        // Search all members at once, as we need to show selected members on top
        // And as this feature is only for internal tooling, so safe to fetch all members at once
        organizationId: orgId,
        filters: {
          roles: ["admin"],
        },
        orderByDirection: "ASC",
        orderBy: "FIRST_NAME",
      },
    }),
    props: ({ getTeacherFeed }) => {
      const { networkStatus } = getTeacherFeed;
      const queryData = _.get(getTeacherFeed, "node", {});

      return {
        staffEdges: _.get(queryData, "staff.edges", STABLE_EMPTY_ARRAY),
        isData: !_.isEmpty(queryData),
        isLoading: _.includes([1, 2], networkStatus),
      };
    },
  }),
  withLoader
)(List);

const StaffList = props => {
  const {
    enabledUserIds,
    onModalClose,
    orgId,
    setFullScreenLoader,
    moduleId,
  } = props;

  const { t } = useI18n(["common"]);
  const dispatch = useDispatch();

  const [selectedStaffIdsSet, setSelectedStaffIdsSet] = useState(
    new Set(enabledUserIds)
  );

  const handleSave = async () => {
    setFullScreenLoader(true);
    const selectedStaffIds = Array.from(selectedStaffIdsSet);
    const newEnabledUserIds = _.difference(selectedStaffIds, enabledUserIds);
    const disabledUserIds = _.difference(enabledUserIds, selectedStaffIds);

    // If nothing is changed, then close the modal
    if (_.isEmpty(newEnabledUserIds) && _.isEmpty(disabledUserIds)) {
      setFullScreenLoader(false);
      onModalClose();
      return;
    }

    await dispatch(
      updateAddOnStatus({
        orgId,
        updateOn: USER,
        enabledUserIds: newEnabledUserIds,
        disabledUserIds,
        addOnType: MESSAGING,
        moduleId,
        queryVariables: getQueryVariablesForMessaging(orgId),
      })
    );
    setFullScreenLoader(false);
    onModalClose();
  };

  return (
    <Modal
      width="544px"
      height="80vh"
      onClose={onModalClose}
      shouldCloseOnEsc
      isOpen
      shouldCloseOnOverlayClick={false}
    >
      <Modal.Header title={t("common:select_admins")} isClosable hasSeparator />
      <Modal.Body isScrollable style={styles.modalBodyStyle}>
        <ListWithData
          orgId={orgId}
          selectedStaffIdsSet={selectedStaffIdsSet}
          setSelectedStaffIdsSet={setSelectedStaffIdsSet}
        />
      </Modal.Body>
      <Modal.Footer hasSeparator>
        <div className={classes.footer}>
          <div
            className={classNames(
              "text-label-3",
              "text-textLink",
              classes.footerText
            )}
          >
            {t("common:total_staff_selected_with_count", {
              count: selectedStaffIdsSet.size,
            })}
          </div>
          <div className={classes.footerButtons}>
            <Button
              onClick={onModalClose}
              variant="outlined-subtle"
              size="medium"
            >
              {t("common:cancel")}
            </Button>
            <Button onClick={handleSave} variant="progressive" size="medium">
              {t("common:save")}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

StaffList.propTypes = {
  onModalClose: PropTypes.func.isRequired,
  setFullScreenLoader: PropTypes.func.isRequired,
  enabledUserIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  moduleId: PropTypes.oneOf(_.values(MESSAGING_MODULES)).isRequired,
};

export default StaffList;
